import React, {useEffect, useMemo, useState} from 'react';
import {DashboardPluginProps} from "../../../core/plugin/PluginType";
import {List} from "@mui/material";
import Spinner from "../../../ui/Spinner";
import {useMe} from "../../../hooks/useMe";
import {
    ApprovalInstance,
    BusinessActionApprovalInstance,
    TimeRecordRequestApprovalInstance,
    ENTITY_TYPE_METADATA,
    StepInstanceStatus,
    isBusinessActionApprovalInstance,
    isTimeRecordRequestApprovalInstance,
    isLeaveRequestApprovalInstance,
    LeaveRequestApprovalInstance,
} from "../../../api/ApprovalApi";
import {
    EmployeeRelatedBusinessAction,
    isEmployeeRelatedAction,
} from "../../../api/BusinessActionApi";
import ApprovalActions from "./ApprovalActions";
import {Employee} from "../../../api/EmployeeApi";
import {Link} from "../../../components/Link";
import Typography from '../../../components/Typography';
import {Chip} from "../../../components/Chip";
import {MultiSelect, MultiSelectItem} from "../../../components/input/MultiSelect";
import {useWidgetActions} from "../../../core/components/useWidgetActions";
import {LeaveRequestApprovalDetailModal} from './LeaveRequestApprovalDetailModal';
import EmployeeAvatar from "../../../components/EmployeeAvatar";
import {useTranslation} from "react-i18next";
import {DateUtils} from "../../../util/DateUtils";
import {DotIcon} from "../../../components/Icons";
import {TimeRecordApprovalDetailModal} from './TimeRecordApprovalDetailModal';
import {BusinessActionApprovalDetailModal} from "./BusinessActionApprovalDetailModal";

// ── Shared helpers ────────────────────────────────────────────────────────────

const stepSummary = (instance: ApprovalInstance): string => {
    const total = instance.stepInstances.length;
    if (total === 0) return '';
    const terminalStatuses: StepInstanceStatus[] = ['APPROVED', 'REJECTED', 'SKIPPED', 'TIMED_OUT'];
    const done = instance.stepInstances.filter(s => terminalStatuses.includes(s.status)).length;
    return `Schritt ${done + 1} von ${total}`;
};

// ── Shared row shell ──────────────────────────────────────────────────────────

const Separator: React.FC = () => (
    <DotIcon/>
);

const StepChip: React.FC<{ instance: ApprovalInstance }> = ({instance}) => {
    const label = stepSummary(instance);
    if (!label) return null;
    return <Chip label={label} size="small" variant="outlined"/>;
};


// ── BusinessAction subtype row renderers ──────────────────────────────────────

export type BaseApprovalItemProps = {
    approvalInstance: ApprovalInstance;
    employee: Employee | null;
    children: React.ReactNode;
    actions?: React.ReactNode;
};

export function BaseApprovalInstanceItem({
                                             approvalInstance,
                                             employee,
                                             children,
                                             actions
                                         }: BaseApprovalItemProps) {

    const {t} = useTranslation();

    const [submissionDate] = useState<Date>(new Date(approvalInstance.submittedAt));

    const formattedDate = DateUtils.formatDate(submissionDate, {
        dateStyle: 'full',
        timeStyle: 'short'
    });

    return (
        <div key={approvalInstance.id} className="flex items-center py-4 border-b border-gray-200 last:border-b-0">

            {employee && (
                <div className="mr-4">
                    <EmployeeAvatar employee={employee}/>
                </div>
            )}

            <div className="flex-1">
                <div className="flex justify-between items-center gap-4">
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            {employee ? (
                                <Link
                                    href={`/employees/${employee.employeeCode}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant={'inherit'}
                                    underline={'hover'}
                                >
                                    {employee.preferredName}
                                </Link>
                            ) : (
                                <span className="text-gray-400">Neuer Mitarbeiter</span>
                            )}

                            <Chip label={ENTITY_TYPE_METADATA[approvalInstance.entityType]?.label
                                ?? approvalInstance.entityType}
                                  size={'small'} color={'warning'}
                            />
                        </div>

                        <div className="text-sm font-normal text-gray-900">
                            {children}
                        </div>

                        <Typography variant={'caption'} color={'textSecondary'}>
                            {t('request.submittedByWithDate', {
                                name: approvalInstance.requestedBy.employee.preferredName,
                                date: formattedDate,
                            })}
                        </Typography>
                    </div>

                    {actions && (
                        <div className="flex gap-2 shrink-0">
                            {actions}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const EmployeeRelatedBusinessActionContent: React.FC<{
    instance: BusinessActionApprovalInstance;
    businessAction: EmployeeRelatedBusinessAction;
    showDivider: boolean;
    onActionSuccess: () => void;
    onShowDetails: (instance: BusinessActionApprovalInstance) => void;
}> = ({instance, businessAction, showDivider, onActionSuccess, onShowDetails}) => {

    const businessActionDefinition = businessAction.businessActionDefinition;
    const businessActionName = businessActionDefinition.label;

    return (
        <BaseApprovalInstanceItem
            approvalInstance={instance}
            employee={businessAction.employee}
            actions={
                <ApprovalActions
                    instance={instance}
                    onShowDetails={onShowDetails}
                    onSuccess={onActionSuccess}
                />
            }
        >
            {businessActionName}
        </BaseApprovalInstanceItem>
    );
};

const BusinessActionApprovalRow: React.FC<{
    instance: BusinessActionApprovalInstance;
    showDivider: boolean;
    onActionSuccess: () => void;
    onShowDetails: (instance: BusinessActionApprovalInstance) => void;
}> = ({instance, showDivider, onActionSuccess, onShowDetails}) => {

    const {approvableEntity} = instance;

    if (isEmployeeRelatedAction(approvableEntity)) {
        return (
            <EmployeeRelatedBusinessActionContent
                instance={instance}
                businessAction={approvableEntity}
                showDivider={showDivider}
                onActionSuccess={onActionSuccess}
                onShowDetails={onShowDetails}
            />
        );
    }

    return null;
};

// ── Time record request row ───────────────────────────────────────────────────

const TimeRecordApprovalRow: React.FC<{
    instance: TimeRecordRequestApprovalInstance;
    showDivider: boolean;
    onActionSuccess: () => void;
    onShowDetails: (instance: TimeRecordRequestApprovalInstance) => void;
}> = ({instance, showDivider, onActionSuccess, onShowDetails}) => {

    const {t} = useTranslation();

    const {approvableEntity} = instance;

    const recordDate = DateUtils.formatDate(new Date(approvableEntity.recordDate), {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });

    return (
        <BaseApprovalInstanceItem
            approvalInstance={instance}
            employee={approvableEntity.employee}
            actions={
                <ApprovalActions
                    instance={instance}
                    onShowDetails={onShowDetails}
                    onSuccess={onActionSuccess}
                />
            }
        >
            {t('approvals.pendingApprovalItems.details.timeRecordRequest', {
                date: recordDate
            })}
        </BaseApprovalInstanceItem>
    );
};


// ── Leave request row ─────────────────────────────────────────────────────────

const LeaveRequestApprovalRow: React.FC<{
    instance: LeaveRequestApprovalInstance;
    showDivider: boolean;
    onActionSuccess: () => void;
    onShowDetails: (instance: LeaveRequestApprovalInstance) => void;
}> = ({instance, showDivider, onActionSuccess, onShowDetails}) => {

    const {t} = useTranslation();
    const {approvableEntity} = instance;

    const leaveStart = DateUtils.formatDate(new Date(approvableEntity.leaveStart), {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });

    const leaveEnd = DateUtils.formatDate(new Date(approvableEntity.leaveEnd), {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });

    return (
        <BaseApprovalInstanceItem
            approvalInstance={instance}
            employee={approvableEntity.employee}
            actions={
                <ApprovalActions
                    instance={instance}
                    onShowDetails={onShowDetails}
                    onSuccess={onActionSuccess}
                />
            }
        >
            {t('approvals.pendingApprovalItems.details.leaveRequest', {
                start: leaveStart,
                end: leaveEnd,
                days: 3
            })}
        </BaseApprovalInstanceItem>
    );
};

// ── Top-level row dispatcher ──────────────────────────────────────────────────

interface ApprovalRowProps {
    instance: ApprovalInstance;
    showDivider: boolean;
    onActionSuccess: () => void;
    onShowTimeRecordDetails: (instance: TimeRecordRequestApprovalInstance) => void;
    onShowBusinessActionDetails: (instance: BusinessActionApprovalInstance) => void;
    onShowLeaveRequestDetails: (instance: LeaveRequestApprovalInstance) => void;
}

const ApprovalRow: React.FC<ApprovalRowProps> = ({
                                                     instance,
                                                     showDivider,
                                                     onActionSuccess,
                                                     onShowTimeRecordDetails,
                                                     onShowBusinessActionDetails,
                                                     onShowLeaveRequestDetails,
                                                 }) => {

    if (isBusinessActionApprovalInstance(instance)) {
        return (
            <BusinessActionApprovalRow
                instance={instance}
                showDivider={showDivider}
                onActionSuccess={onActionSuccess}
                onShowDetails={onShowBusinessActionDetails}
            />
        );
    }

    if (isTimeRecordRequestApprovalInstance(instance)) {
        return (
            <TimeRecordApprovalRow
                instance={instance}
                showDivider={showDivider}
                onActionSuccess={onActionSuccess}
                onShowDetails={onShowTimeRecordDetails}
            />
        );
    }

    if (isLeaveRequestApprovalInstance(instance)) {
        return (
            <LeaveRequestApprovalRow
                instance={instance}
                showDivider={showDivider}
                onActionSuccess={onActionSuccess}
                onShowDetails={onShowLeaveRequestDetails}
            />
        );
    }

    return null;
};

// ── Widget ────────────────────────────────────────────────────────────────────

const ENTITY_TYPE_OPTIONS: MultiSelectItem[] = [
    {id: 'BUSINESS_ACTION', name: 'Geschäftsvorgang'},
    {id: 'TIME_RECORD_REQUEST', name: 'Arbeitszeitkorrektur'},
    {id: 'LEAVE_REQUEST', name: 'Urlaubsantrag'},
];

const PendingApprovalsWidget: React.FC<DashboardPluginProps> = ({size, context, widgetId}) => {

    const [selectedTimeRecord, setSelectedTimeRecord] =
        useState<TimeRecordRequestApprovalInstance | null>(null);
    const [selectedBusinessAction, setSelectedBusinessAction] =
        useState<BusinessActionApprovalInstance | null>(null);
    const [selectedLeaveRequest, setSelectedLeaveRequest] =
        useState<LeaveRequestApprovalInstance | null>(null);

    const {
        pendingApprovals,
        getPendingApprovals
    } = useMe();

    // Default: all types selected
    const [selectedTypes, setSelectedTypes] = useState<(string | number)[]>(
        ENTITY_TYPE_OPTIONS.map((o) => o.id),
    );

    useEffect(() => {
        getPendingApprovals();
    }, [getPendingApprovals]);

    const typeFilterAction = useMemo(
        () => (
            <MultiSelect
                variant="embedded"
                placeholder="Alle Typen"
                items={ENTITY_TYPE_OPTIONS}
                selectedKeys={selectedTypes}
                onSelectionChange={setSelectedTypes}
                allowsSearch={false}
                maxVisibleSelections={2}
            />
        ),
        [selectedTypes],
    );

    useWidgetActions(() => typeFilterAction, [typeFilterAction]);

    if (pendingApprovals.loading || !pendingApprovals.loaded) {
        return <Spinner/>;
    }

    if (pendingApprovals.error) {
        return (
            <Typography variant="body2" color="error">
                Fehler beim Laden der ausstehenden Freigaben.
            </Typography>
        );
    }

    const maxItems = size === 'small' ? 3 : size === 'medium' ? 5 : 8;

    const selectedSet = new Set(selectedTypes.map(String));
    const filteredItems = (pendingApprovals.data ?? [])
        .filter(instance => selectedSet.size === 0 || selectedSet.has(instance.entityType))
        .slice(0, maxItems);

    if (filteredItems.length === 0) {
        return (
            <Typography variant="body2" color="secondary">
                {selectedTypes.length === 0
                    ? 'Kein Typ ausgewählt.'
                    : 'Keine ausstehenden Freigaben.'}
            </Typography>
        );
    }

    const handleTimeRecordSuccess = (updated: ApprovalInstance) => {
        setSelectedTimeRecord(null);
        getPendingApprovals();
    };

    const onActionSuccess = async (): Promise<void> => {
        console.log('Action Success');
        await getPendingApprovals();
    };

    return (
        <>
            <List disablePadding className="pending-approvals-widget">
                {filteredItems.map((instance, index) => (
                    <ApprovalRow
                        key={instance.id}
                        instance={instance}
                        showDivider={index < filteredItems.length - 1}
                        onActionSuccess={onActionSuccess}
                        onShowTimeRecordDetails={setSelectedTimeRecord}
                        onShowBusinessActionDetails={setSelectedBusinessAction}
                        onShowLeaveRequestDetails={setSelectedLeaveRequest}
                    />
                ))}
            </List>

            {selectedTimeRecord && (
                <TimeRecordApprovalDetailModal
                    approvalInstance={selectedTimeRecord}
                    open={true}
                    onClose={() => setSelectedTimeRecord(null)}
                    onSuccess={handleTimeRecordSuccess}
                />
            )}

            {selectedBusinessAction && (
                <BusinessActionApprovalDetailModal
                    instance={selectedBusinessAction}
                    open={true}
                    onClose={() => setSelectedBusinessAction(null)}
                    onSuccess={() => {
                        setSelectedBusinessAction(null);
                        getPendingApprovals();
                    }}
                />
            )}

            {selectedLeaveRequest && (
                <LeaveRequestApprovalDetailModal
                    instance={selectedLeaveRequest}
                    open={true}
                    onClose={() => setSelectedLeaveRequest(null)}
                    onSuccess={() => {
                        setSelectedLeaveRequest(null);
                        getPendingApprovals();
                    }}
                />
            )}
        </>
    );
};

export default PendingApprovalsWidget;